import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { Theme } from "@mui/material/styles";
import { WithStyles } from "@mui/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import Drawer, { DrawerProps } from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { DistributiveOmit } from "@mui/types";
import { Link, useLocation } from "react-router-dom";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { Permission } from "common/build/prisma/client";
import { useAuth } from "../../contexts/auth.context";
import { useAppView } from "../../contexts/appView.context";
//import { useSetTourState, useTrackedTourState } from "../tour/Store";
import VerbalLogo from "../../img/VerbalLogo";
import { verbalTheme } from "./themes/verbal.theme";
import Tooltip from "@mui/material/Tooltip";
import LogoV from "../../img/logo-v.png";
import { Divider, MenuItem, Paper, Popper } from "@mui/material";
import {
    Book,
    BookmarkBorder,
    ChevronLeft,
    ChevronRight,
    CollectionsBookmark,
    //Dashboard,
    ExitToApp,
    ImportContacts,
    Domain,
    PieChart,
    PeopleOutlined,
    Settings,
    Style,
    ViewModule,
    HomeOutlined,
    HelpOutline,
    ContactSupportOutlined,
    WebOutlined,
    QuestionAnswerOutlined,
    Close,
    OndemandVideoOutlined,
    SchoolOutlined,
    Explore,
    LibraryBooks,
    Build,
    DirectionsWalk,
    AccountTree,
    AssessmentOutlined,
    QuizOutlined,
} from "@mui/icons-material";
import MapIcon from "@mui/icons-material/Map";
import { useOrganisation } from "../../hooks/useOrganisation";
import largeV from "../../img/large-v-nav.svg";
import { useMobile } from "../../hooks/useMobile";
import { TutorialVideosDialog } from "../support/TutorialVideosDialog";
import { getCustomerPortalUrl } from "../../client/api/public/stripe";
import { SubscriptionContactDialog } from "../organisation/SubscriptionContactDialog";
import { SubscriptionExpiredDialog } from "../organisation/SubscriptionExpiredDialog";
import { useSetTourState, useTrackedTourState } from "../tour/Store";
import { SCHOOL_CREATE_PROGRAMME_PATH, useNextSession } from "../../hooks/useNextSession";
import { isAssessPreviewEnabled, isSurveyBuilderOpenToAllOrgs } from "../../config/featureFlags";
import { isSoloPersonalOrganisation, organisationLicenceHasExpired } from "../../utils/courseDemo";
import { shouldShowNavItem } from "../../utils/appView";

export type categoryProps = {
    id: string;
    icon: React.ReactElement;
    path: string;
    permission: Permission;
};
const drawerWidth = 260;
// Exported for the scroll regression test — jsdom computes no layout, so the rule that keeps
// Logout reachable is asserted against the style object rather than a rendered drawer.
export const styles = (theme: Theme) =>
    createStyles({
        drawer: {
            width: drawerWidth,
            flexShrink: 0,
            whiteSpace: "nowrap",
            zIndex: 0,
        },
        drawerPaper: {
            justifyContent: "space-between",
            backgroundColor: verbalTheme.palette.secondary.main,
            backgroundImage: `url(${largeV})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center bottom",
        },
        drawerOpen: {
            transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            // Horizontal only. `overflow: hidden` here clipped the whole paper, so on a viewport
            // shorter than the menu the bottom block — Get Help, Collapse and LOGOUT — was
            // unreachable: no scrollbar, no way to sign out without clearing the session by hand.
            // The X axis still has to be hidden or the labels produce a scrollbar mid-transition
            // while the width animates, which is what `drawerClose` below has always done.
            overflowX: "hidden",
            overflowY: "auto",
            width: drawerWidth,
        },
        drawerClose: {
            transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            overflowX: "hidden",
            width: `calc(${theme.spacing(6)} + 1px)`,
            [theme.breakpoints.up("sm")]: {
                width: `calc(${theme.spacing(8)} + 5px)`,
            },
        },
        subDrawerOpen: {
            width: drawerWidth,
        },
        subDrawerHeader: {
            display: "flex",
            padding: theme.spacing(0, 1),
        },
        item: {
            paddingTop: verbalTheme.spacing(2),
            paddingBottom: verbalTheme.spacing(2),
            paddingLeft: verbalTheme.spacing(3),
            color: "rgba(255, 255, 255, 0.7)",
            "&:hover,&:focus": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
            },
        },
        itemLogo: {
            paddingTop: "24px",
            paddingBottom: "24px",
            paddingRight: "32px",
        },
        itemActiveItem: {
            color: verbalTheme.palette.primary.main,
            backgroundColor: "rgba(255, 255, 255, 0.08)",
        },
        itemPrimary: {
            fontSize: "inherit",
            fontWeight: "bold",
            whiteSpace: "normal",
        },
        itemIcon: {
            minWidth: "auto",
            marginRight: theme.spacing(3),
            color: "inherit",
        },
        bottom: {
            position: "absolute",
            bottom: 0,
        },
        menuButton: {
            color: "#ffffff",
        },
        hide: {
            display: "none",
        },
        logoSmall: {},
        logoOpen: {
            "& .logoFull": {
                display: "block",
            },
            "& .logoSmall": {
                display: "none",
            },
        },
        logoClose: {
            "& .logoFull": {
                display: "none",
            },
            "& .logoSmall": {
                display: "block",
                width: "36px",
            },
            paddingRight: "16px",
        },
        toolbar: theme.mixins.toolbar,
    });
// Helper function to check if route is active - takes location as parameter to avoid hook violations
export const activeRoute = (routeName?: string, pathname?: string): boolean => {
    if (!pathname) return false;
    const lowerRoute = routeName ? routeName.toLowerCase() : "";
    const lowerPath = pathname.toLowerCase();
    const createProgrammePath = SCHOOL_CREATE_PROGRAMME_PATH.toLowerCase();

    // Prevent highlighting My Programmes when specifically on the Create Programme route
    if (lowerRoute === "/programmes") {
        return lowerPath.startsWith("/programmes") && lowerPath !== "/programmes/add";
    }

    if (lowerRoute === createProgrammePath) {
        return lowerPath === createProgrammePath;
    }

    if (lowerRoute === "/classrooms") {
        return lowerPath.startsWith("/classrooms") && !lowerPath.startsWith(createProgrammePath);
    }

    return lowerPath.indexOf(lowerRoute) > -1 ? true : false;
};
export interface NavigatorProps extends DistributiveOmit<DrawerProps, "classes">, WithStyles<typeof styles> {}
function Navigator(props: NavigatorProps) {
    const location = useLocation(); // Get location once at component level
    const { logout, hasPermission, user } = useAuth();
    const { activeView, canSwitch } = useAppView();
    const setTourState = useSetTourState();
    const tourState = useTrackedTourState();
    const { classes, ...other } = props;
    const { organisation, getOrganisation } = useOrganisation();
    const { isMobile } = useMobile();

    const { preparePath, deliverPath } = useNextSession();

    useEffect(() => {
        if (user) {
            getOrganisation(user.id);
        }
    }, [user]);
    const { push } = useAppNavigate();
    const [open, setOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [subMenuOpen, setSubMenuOpen] = useState<Map<string, boolean>>(
        new Map<string, boolean>([
            ["tours", false],
            ["help", false],
            ["story", false],
            ["stats", false],
            ["survey", false],
        ]),
    );
    const [videosOpen, setVideosOpen] = useState<boolean>(false);
    // Subscription Dialog
    const [subDialogOpen, setSubDialogOpen] = useState<boolean>(false);
    const [subExpiredDialogOpen, setSubExpiredDialogOpen] = useState<boolean>(false);
    const handleDrawerOpen = () => {
        setSubMenuOpen(
            new Map(
                subMenuOpen
                    .set("tours", false)
                    .set("help", false)
                    .set("story", false)
                    .set("stats", false)
                    .set("survey", false),
            ),
        );
        setAnchorEl(null);
        setOpen(true);
    };
    const handleDrawerClose = () => {
        setOpen(false);
    };
    useEffect(() => {
        if (isMobile) {
            setOpen(true);
        }
    }, [isMobile]);
    const useStyles = makeStyles(
        createStyles({
            popper: {
                minHeight: "100% !important",
                transform: `translate3d(${!open ? "219px" : "68px"}, 0px, 0px) !important`,
                zIndex: 1,
                position: "fixed",
            },
            menuPaper: {
                width: "240px",
                minHeight: "100vh",
                paddingBottom: 0,
                marginBottom: 0,
                borderRadius: 0,
                backgroundColor: verbalTheme.palette.secondary.main,
                backgroundImage: `url(${largeV})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: `${!open ? "166% bottom" : "128% bottom"}`,
            },
            subMenuHeader: {
                paddingTop: !open ? verbalTheme.spacing(4) : verbalTheme.spacing(3),
                paddingBottom: !open ? verbalTheme.spacing(4) : verbalTheme.spacing(3),
                paddingLeft: verbalTheme.spacing(3),
                color: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                justifyContent: "flex-end",
            },
        }),
    );
    const localClasses = useStyles();
    const handleMyAccount = async () => {
        const isAdmin = user?.Role.name === "Admin";
        if (
            isAdmin &&
            organisation &&
            (organisation.subscriptionStatus === "active" || organisation?.subscriptionStatus === "trialing") &&
            organisation.stripeCustomerId
        ) {
            await getCustomerPortalUrl(organisation.stripeCustomerId).then(({ url }) => window.open(url, "_blank"));
        } else {
            setSubDialogOpen(true);
        }
    };
    const menuChildren: categoryProps[] = [
        { id: "Home", icon: <HomeOutlined />, path: "/home", permission: "DashboardView" },
        { id: "Explore", icon: <Explore />, path: "/explore", permission: "DashboardView" },
    ];

    if (user?.isSchool) {
        menuChildren.push(
            {
                id: "Classrooms",
                icon: <SchoolOutlined />,
                path: "/classrooms",
                permission: "ClassroomView",
            },
            { id: "Prepare", icon: <ImportContacts />, path: preparePath, permission: "ProgrammeViewAll" },
            { id: "Deliver", icon: <OndemandVideoOutlined />, path: deliverPath, permission: "ProgrammeViewAll" },
            {
                id: "My participants",
                icon: <PeopleOutlined />,
                path: "/participants",
                permission: "ProgrammeViewAll",
            },
            {
                id: "Create programme",
                icon: <CollectionsBookmark />,
                path: SCHOOL_CREATE_PROGRAMME_PATH,
                permission: "ProgrammeCreate",
            },
        );
    } else {
        menuChildren.push(
            { id: "Prepare", icon: <ImportContacts />, path: preparePath, permission: "ProgrammeViewAll" },
            { id: "Deliver", icon: <OndemandVideoOutlined />, path: deliverPath, permission: "ProgrammeViewAll" },
            { id: "My Programmes", icon: <BookmarkBorder />, path: "/programmes", permission: "ProgrammeViewAll" },
            { id: "My participants", icon: <PeopleOutlined />, path: "/participants", permission: "ProgrammeViewAll" },
            {
                id: "Create programme",
                icon: <CollectionsBookmark />,
                // Always the shipped wizard. VER-128 pointed this at the design preview whenever
                // the flag was on, which on staging left no route to a real programme at all — the
                // stepper is mock-only, so creating one became impossible for anyone testing there.
                // The preview keeps its own route and is opened by URL.
                path: "/programmes/add",
                permission: "ProgrammeCreate",
            },
        );
    }

    // Individuals cannot invite staff. Partner orgs (including future Demo orgs) can.
    const isSoloPersonalOrg = isSoloPersonalOrganisation(organisation ?? user?.Organisation, user?.isSchool);
    if (!isSoloPersonalOrg) {
        menuChildren.push({
            id: "Add User",
            icon: <PeopleOutlined />,
            path: "/add-users",
            permission: "SettingsView",
        });
    }

    // Assess hub — VER-53 Phase 3. Partner orgs only: School Admin is FullAccess, so a
    // SurveyViewAll check would still show an unfinished worklist of every classroom programme.
    if (!user?.isSchool) {
        menuChildren.push({
            id: "Assess",
            icon: <AssessmentOutlined />,
            path: "/assess",
            permission: "SurveyViewAll",
        });
    }

    const categories = [
        {
            id: "Menu",
            children: menuChildren,
        },
        {
            id: "Account",
            children: (isSoloPersonalOrg
                ? []
                : [
                      { id: "User Accounts", icon: <Settings />, path: "/users", permission: "SettingsView" },
                  ]) as categoryProps[],
        },
    ];
    const storyCategories = [
        {
            id: "StoryCuration",
            children: [
                { id: "Stories", icon: <Book />, path: "/stories", permission: "StoryViewAll" },
                {
                    id: "Branched Stories",
                    icon: <AccountTree />,
                    path: "/curation/branched-stories",
                    permission: "StoryViewAll",
                    badge: "NEW",
                },
                {
                    id: "Collections",
                    icon: <CollectionsBookmark />,
                    path: "/collections",
                    permission: "StoryCollectionView",
                },
                { id: "Curriculums", icon: <ViewModule />, path: "/curriculums", permission: "StoryCollectionCreate" },
                { id: "Themes", icon: <Style />, path: "/themes", permission: "StoryCollectionCreate" },
            ] as categoryProps[],
        },
    ];
    // Surveys — VER-108 Builder + VER-109 Results, rendered in the shared Popper flyout.
    // Gated on SurveyViewAll, matching the /survey/* route permits.
    //
    // The Builder is internal Verbal tooling (it authors the instruments Verbal ships to partner
    // organisations), so it is additionally gated on isVerbal — organisation name === "Verbal",
    // the same flag that gates Story Curation and Stats below. NOT isSchool, which is orgType.
    // The /survey/builder route carries the same `verbalOnly` guard: this only hides the link.
    const surveyCategories = [
        {
            id: "Surveys",
            children: [
                // The `||` is the TEMPORARY staging override — see isSurveyBuilderOpenToAllOrgs().
                ...(user?.isVerbal || isSurveyBuilderOpenToAllOrgs()
                    ? [
                          {
                              id: "Survey Builder",
                              icon: <Build />,
                              path: "/survey/builder",
                              permission: "SurveyViewAll",
                          },
                      ]
                    : []),
                // Survey Results must follow the SAME preview flag as its route. Routes.tsx only
                // registers /survey/results when the flag is on, the admin router has no catch-all,
                // and the participant router carries a passthrough for that path — so an
                // unconditional link produced a completely blank page in any build with the flag
                // off (no 403, no 404). AssessHub already reasons about this for its own buttons.
                ...(isAssessPreviewEnabled()
                    ? [
                          {
                              id: "Survey Results",
                              icon: <AssessmentOutlined />,
                              path: "/survey/results",
                              permission: "SurveyViewAll",
                          },
                      ]
                    : []),
            ] as categoryProps[],
        },
    ];
    // The flyout trigger must not open onto an empty menu, so it follows whether any child
    // survives its permit. The isVerbal and preview gates are applied while BUILDING the children
    // above, so reading the assembled list here cannot drift from what actually renders — checking
    // the permits alone would have missed both gates.
    const hasVisibleSurveyChild = surveyCategories.some(({ children }) =>
        children.some(({ permission }) => hasPermission(permission as Permission)),
    );
    const statsCategories = [
        {
            id: "Stats",
            children: [
                { id: "Organisations", icon: <Domain />, path: "/stats/organisations", permission: "StatsViewAll" },
                {
                    id: "Story Collections",
                    icon: <CollectionsBookmark />,
                    path: "/stats/storycollections",
                    permission: "StatsViewAll",
                },
            ] as categoryProps[],
        },
    ];
    return (
        <>
            {/** Main Drawer */}
            <Drawer
                variant="permanent"
                className={clsx(classes.drawer, "navigatorContainer", {
                    [classes.drawerOpen]: !open,
                    [classes.drawerClose]: open,
                })}
                classes={{
                    paper: clsx(classes.drawerPaper, {
                        [classes.drawerOpen]: !open,
                        [classes.drawerClose]: open,
                    }),
                }}
                {...other}
            >
                <div id="main-nav-top">
                    {organisation && organisation.Tier?.tier === "Demo" && <div className={classes.toolbar} />}
                    {organisation && organisationLicenceHasExpired(organisation, user?.isVerbal) && (
                        <div className={classes.toolbar} />
                    )}
                    {canSwitch && <div className={classes.toolbar} />}
                    <List disablePadding component={"nav"}>
                        <ListItem
                            className={clsx(classes.itemLogo, {
                                [classes.logoOpen]: !open,
                                [classes.logoClose]: open,
                            })}
                            onClick={() => push("/home")}
                        >
                            <img className={"logoSmall"} src={LogoV} />
                            <div className={"logoFull"}>
                                <VerbalLogo color={"white"} />
                            </div>
                        </ListItem>
                        {categories.map(({ id, children }) => (
                            <React.Fragment key={id}>
                                {children.map(
                                    ({ id: childId, icon, path, permission }) =>
                                        hasPermission(permission) &&
                                        shouldShowNavItem(childId, activeView, user?.isSchool) && (
                                            <ListItem
                                                component={Link}
                                                to={path}
                                                key={childId}
                                                className={clsx(
                                                    classes.item,
                                                    activeRoute(path, location.pathname) &&
                                                        !subMenuOpen.get("story") &&
                                                        !subMenuOpen.get("help") &&
                                                        !subMenuOpen.get("stats") &&
                                                        !subMenuOpen.get("tours") &&
                                                        !subMenuOpen.get("survey") &&
                                                        classes.itemActiveItem,
                                                    {
                                                        ["nav" + childId.split(" ").join("") + "Button"]: true,
                                                    },
                                                )}
                                                onClick={() =>
                                                    setSubMenuOpen(
                                                        new Map(
                                                            subMenuOpen
                                                                .set("tours", false)
                                                                .set("help", false)
                                                                .set("story", false)
                                                                .set("stats", false)
                                                                .set("survey", false),
                                                        ),
                                                    )
                                                }
                                            >
                                                <Tooltip title={childId}>
                                                    <ListItemIcon className={classes.itemIcon}>{icon}</ListItemIcon>
                                                </Tooltip>
                                                <ListItemText
                                                    classes={{
                                                        primary: classes.itemPrimary,
                                                    }}
                                                >
                                                    {childId}
                                                </ListItemText>
                                            </ListItem>
                                        ),
                                )}
                            </React.Fragment>
                        ))}
                        {/* Surveys — second-level flyout (VER-108 Builder + VER-109 Results),
                            mirroring the Story Curation / Stats submenu pattern. */}
                        {hasVisibleSurveyChild && (
                            <ListItem
                                className={clsx(
                                    classes.item,
                                    (activeRoute("/survey", location.pathname) || subMenuOpen.get("survey")) &&
                                        classes.itemActiveItem,
                                    "navSurveysButton",
                                )}
                                onClick={(event: React.MouseEvent<HTMLLIElement>) => {
                                    setAnchorEl(event.currentTarget);
                                    setSubMenuOpen(
                                        new Map(
                                            subMenuOpen
                                                .set("survey", !subMenuOpen.get("survey"))
                                                .set("tours", false)
                                                .set("help", false)
                                                .set("story", false)
                                                .set("stats", false),
                                        ),
                                    );
                                }}
                            >
                                <Tooltip title="Surveys">
                                    <ListItemIcon className={classes.itemIcon}>
                                        <QuizOutlined />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText classes={{ primary: classes.itemPrimary }}>Surveys</ListItemText>
                            </ListItem>
                        )}
                        {user && user.isSchool && !user.isVerbal && (
                            <>
                                <ListItem
                                    className={clsx(classes.item)}
                                    onClick={() => {
                                        handleMyAccount();
                                    }}
                                >
                                    <Tooltip title="My Account">
                                        <ListItemIcon className={classes.itemIcon}>
                                            <LibraryBooks />
                                        </ListItemIcon>
                                    </Tooltip>
                                    <ListItemText classes={{ primary: classes.itemPrimary }}>My Account</ListItemText>
                                </ListItem>
                            </>
                        )}
                        {user && user.isVerbal && hasPermission("StoryViewAll") && (
                            <ListItem
                                className={clsx(
                                    classes.item,
                                    (activeRoute("/stories", location.pathname) ||
                                        activeRoute("/collections", location.pathname) ||
                                        activeRoute("/curriculums", location.pathname) ||
                                        activeRoute("/themes", location.pathname) ||
                                        subMenuOpen.get("story")) &&
                                        classes.itemActiveItem,
                                    "navStoryCurationButton",
                                )}
                                onClick={(event: React.MouseEvent<HTMLLIElement>) => {
                                    setAnchorEl(event.currentTarget);
                                    setSubMenuOpen(
                                        new Map(
                                            subMenuOpen
                                                .set("story", !subMenuOpen.get("story"))
                                                .set("tours", false)
                                                .set("help", false)
                                                .set("stats", false)
                                                .set("survey", false),
                                        ),
                                    );
                                }}
                            >
                                <Tooltip title={"Story Curation"}>
                                    <ListItemIcon className={classes.itemIcon}>
                                        <ImportContacts />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText classes={{ primary: classes.itemPrimary }}>Story Curation</ListItemText>
                            </ListItem>
                        )}
                        {user && user.isVerbal && hasPermission("StatsViewAll") && (
                            <ListItem
                                className={clsx(
                                    classes.item,
                                    (activeRoute("/stats", location.pathname) || subMenuOpen.get("stats")) &&
                                        classes.itemActiveItem,
                                    "navStatsButton",
                                )}
                                onClick={(event: React.MouseEvent<HTMLLIElement>) => {
                                    setAnchorEl(event.currentTarget);
                                    setSubMenuOpen(
                                        new Map(
                                            subMenuOpen
                                                .set("stats", !subMenuOpen.get("stats"))
                                                .set("tours", false)
                                                .set("help", false)
                                                .set("story", false)
                                                .set("survey", false),
                                        ),
                                    );
                                }}
                            >
                                <Tooltip title={"Stats"}>
                                    <ListItemIcon className={classes.itemIcon}>
                                        <PieChart />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText classes={{ primary: classes.itemPrimary }}>Stats</ListItemText>
                            </ListItem>
                        )}
                    </List>
                </div>
                <List disablePadding component={"nav"} id="main-nav-bottom">
                    {tourState.currentTour && (
                        <ListItem
                            className={clsx(classes.item, "navTakeTourButton")}
                            onClick={() => {
                                setTourState((prev) => ({ ...prev, openTour: !prev.openTour }));
                            }}
                        >
                            <Tooltip title="Tour">
                                <ListItemIcon className={classes.itemIcon}>
                                    <DirectionsWalk />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText classes={{ primary: classes.itemPrimary }}>Take Tour</ListItemText>
                        </ListItem>
                    )}
                    <ListItem
                        className={clsx(classes.item, subMenuOpen.get("tours") && classes.itemActiveItem)}
                        onClick={(event: React.MouseEvent<HTMLLIElement>) => {
                            setAnchorEl(event.currentTarget);
                            setSubMenuOpen(
                                new Map(
                                    subMenuOpen
                                        .set("tours", !subMenuOpen.get("tours"))
                                        .set("story", false)
                                        .set("stats", false)
                                        .set("help", false)
                                        .set("survey", false),
                                ),
                            );
                        }}
                    >
                        <Tooltip title="Tour Guide">
                            <ListItemIcon className={classes.itemIcon}>
                                <MapIcon />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText classes={{ primary: classes.itemPrimary }}>Tour Guide</ListItemText>
                    </ListItem>
                    <ListItem
                        className={clsx(classes.item, subMenuOpen.get("help") && classes.itemActiveItem)}
                        onClick={(event: React.MouseEvent<HTMLLIElement>) => {
                            setAnchorEl(event.currentTarget);
                            setSubMenuOpen(
                                new Map(
                                    subMenuOpen
                                        .set("help", !subMenuOpen.get("help"))
                                        .set("tours", false)
                                        .set("story", false)
                                        .set("stats", false)
                                        .set("survey", false),
                                ),
                            );
                        }}
                    >
                        <Tooltip title="Get Help">
                            <ListItemIcon className={classes.itemIcon}>
                                <HelpOutline />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText classes={{ primary: classes.itemPrimary }}>Get Help</ListItemText>
                    </ListItem>
                    <ListItem
                        onClick={handleDrawerClose}
                        className={clsx(classes.item, {
                            [classes.hide]: !open,
                        })}
                    >
                        <Tooltip title="Expand Nav">
                            <ListItemIcon className={classes.itemIcon}>
                                <ChevronRight />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText
                            classes={{
                                primary: classes.itemPrimary,
                            }}
                        >
                            Expand
                        </ListItemText>
                    </ListItem>
                    <ListItem
                        onClick={handleDrawerOpen}
                        className={clsx(classes.item, {
                            [classes.hide]: open,
                        })}
                    >
                        <Tooltip title="Collapse Nav">
                            <ListItemIcon className={classes.itemIcon}>
                                <ChevronLeft />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText
                            classes={{
                                primary: classes.itemPrimary,
                            }}
                        >
                            Collapse
                        </ListItemText>
                    </ListItem>
                    <ListItem onClick={logout} className={clsx(classes.item)}>
                        <Tooltip title="Logout">
                            <ListItemIcon className={classes.itemIcon}>
                                <ExitToApp />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText classes={{ primary: classes.itemPrimary }}>Logout</ListItemText>
                    </ListItem>
                </List>
            </Drawer>
            {/** Sub Drawer */}
            <Popper
                anchorEl={anchorEl}
                placement={"right-end"}
                keepMounted
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                popperOptions={{ modifiers: [{ name: "preventOverflow", options: { boundary: "viewport" } }] } as any}
                role={undefined}
                className={localClasses.popper}
                open={
                    (subMenuOpen.get("tours") ||
                        subMenuOpen.get("help") ||
                        subMenuOpen.get("story") ||
                        subMenuOpen.get("stats") ||
                        subMenuOpen.get("survey")) ??
                    false
                }
            >
                <Paper className={localClasses.menuPaper} elevation={0}>
                    <MenuItem
                        onClick={() => {
                            setSubMenuOpen(
                                new Map(
                                    subMenuOpen
                                        .set("tours", false)
                                        .set("help", false)
                                        .set("story", false)
                                        .set("stats", false)
                                        .set("survey", false),
                                ),
                            );
                            setAnchorEl(null);
                        }}
                        className={localClasses.subMenuHeader}
                    >
                        <ListItemIcon className={classes.itemIcon}>
                            <Close />
                        </ListItemIcon>
                    </MenuItem>
                    <Divider style={{ marginBottom: "8px" }} />
                    {subMenuOpen.get("tours") ? (
                        <>
                            <MenuItem
                                component={Link}
                                to="/home"
                                className={clsx(
                                    classes.item,
                                    activeRoute("/home", location.pathname) && classes.itemActiveItem,
                                )}
                                onClick={() => {
                                    setSubMenuOpen(
                                        new Map(
                                            subMenuOpen
                                                .set("tours", false)
                                                .set("help", false)
                                                .set("stats", false)
                                                .set("story", false)
                                                .set("survey", false),
                                        ),
                                    );
                                    setAnchorEl(null);
                                    setTourState((prev) => ({ ...prev, openTour: true }));
                                }}
                            >
                                <Tooltip title="Tour Home">
                                    <ListItemIcon className={classes.itemIcon}>
                                        <DirectionsWalk />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText classes={{ primary: classes.itemPrimary }}>Tour Home</ListItemText>
                            </MenuItem>
                            <MenuItem
                                component={Link}
                                to="/classrooms"
                                className={clsx(
                                    classes.item,
                                    activeRoute("/classrooms", location.pathname) && classes.itemActiveItem,
                                )}
                                onClick={() => {
                                    setSubMenuOpen(
                                        new Map(
                                            subMenuOpen
                                                .set("tours", false)
                                                .set("help", false)
                                                .set("stats", false)
                                                .set("story", false)
                                                .set("survey", false),
                                        ),
                                    );
                                    setAnchorEl(null);
                                    setTourState((prev) => ({ ...prev, openTour: true }));
                                }}
                            >
                                <Tooltip title="Tour Classrooms">
                                    <ListItemIcon className={classes.itemIcon}>
                                        <DirectionsWalk />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText classes={{ primary: classes.itemPrimary }}>Tour Classrooms</ListItemText>
                            </MenuItem>
                            <MenuItem
                                component={Link}
                                to="/add-users"
                                className={clsx(
                                    classes.item,
                                    activeRoute("/add-users", location.pathname) && classes.itemActiveItem,
                                )}
                                onClick={() => {
                                    setSubMenuOpen(
                                        new Map(
                                            subMenuOpen
                                                .set("tours", false)
                                                .set("help", false)
                                                .set("stats", false)
                                                .set("story", false)
                                                .set("survey", false),
                                        ),
                                    );
                                    setAnchorEl(null);
                                    setTourState((prev) => ({ ...prev, openTour: true }));
                                }}
                            >
                                <Tooltip title="Tour Add User">
                                    <ListItemIcon className={classes.itemIcon}>
                                        <DirectionsWalk />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText classes={{ primary: classes.itemPrimary }}>Tour Add User</ListItemText>
                            </MenuItem>
                            {RegExp(/^\/classrooms\/view\/\d+\/view\/\d+$/i).test(location.pathname) &&
                                tourState.currentTour && (
                                    <MenuItem
                                        className={clsx(classes.item, "navTakeTourButton")}
                                        onClick={() => {
                                            setSubMenuOpen(
                                                new Map(
                                                    subMenuOpen
                                                        .set("tours", !subMenuOpen.get("tours"))
                                                        .set("story", false)
                                                        .set("stats", false)
                                                        .set("help", false)
                                                        .set("survey", false),
                                                ),
                                            );
                                            setAnchorEl(null);
                                            setTourState((prev) => ({
                                                ...prev,
                                                nextTourKey: "deliverSessionPart1",
                                            }));
                                            setTimeout(() => {
                                                setTourState((prev) => ({
                                                    ...prev,
                                                    openTour: true,
                                                }));
                                            }, 300);
                                        }}
                                    >
                                        <Tooltip title="Tour">
                                            <ListItemIcon className={classes.itemIcon}>
                                                <Build />
                                            </ListItemIcon>
                                        </Tooltip>
                                        <ListItemText classes={{ primary: classes.itemPrimary }}>
                                            Tour Session
                                        </ListItemText>
                                    </MenuItem>
                                )}
                        </>
                    ) : null}
                    {subMenuOpen.get("help") ? (
                        <>
                            <MenuItem className={clsx(classes.item)}>
                                <a
                                    href="https://www.verbalwellbeing.com/frequently-asked-questions/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: "inherit",
                                        textDecoration: "none",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <Tooltip title="Visit Website">
                                        <ListItemIcon className={classes.itemIcon}>
                                            <QuestionAnswerOutlined />
                                        </ListItemIcon>
                                    </Tooltip>
                                    <ListItemText classes={{ primary: classes.itemPrimary }}>FAQs</ListItemText>
                                </a>
                            </MenuItem>
                            <MenuItem className={classes.item} onClick={() => setVideosOpen(true)}>
                                <Tooltip title="Tutorial Videos">
                                    <ListItemIcon className={classes.itemIcon}>
                                        <OndemandVideoOutlined />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText classes={{ primary: classes.itemPrimary }}>Tutorial Videos</ListItemText>
                            </MenuItem>
                            <MenuItem className={clsx(classes.item)}>
                                <a
                                    href="https://www.verbalwellbeing.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: "inherit",
                                        textDecoration: "none",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <Tooltip title="Visit Website">
                                        <ListItemIcon className={classes.itemIcon}>
                                            <WebOutlined />
                                        </ListItemIcon>
                                    </Tooltip>
                                    <ListItemText classes={{ primary: classes.itemPrimary }}>
                                        Visit Website
                                    </ListItemText>
                                </a>
                            </MenuItem>
                            <MenuItem
                                className={clsx(classes.item)}
                                onClick={() => {
                                    if (window.FreshworksWidget) {
                                        window.FreshworksWidget("clear", "ticketForm");
                                        window.FreshworksWidget("identify", "ticketForm", {
                                            name: user?.firstName,
                                            email: user?.email,
                                        });
                                        window.FreshworksWidget("open");
                                    }
                                }}
                            >
                                <Tooltip title="Contact Us">
                                    <ListItemIcon className={classes.itemIcon}>
                                        <ContactSupportOutlined />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText classes={{ primary: classes.itemPrimary }}>Contact Us</ListItemText>
                            </MenuItem>
                        </>
                    ) : null}
                    {subMenuOpen.get("story") ? (
                        <>
                            {storyCategories.map(({ id, children }) => (
                                <React.Fragment key={id}>
                                    {children.map(
                                        ({ id: childId, icon, path, permission }) =>
                                            hasPermission(permission as Permission) && (
                                                <MenuItem
                                                    component={Link}
                                                    to={path}
                                                    key={childId}
                                                    className={clsx(
                                                        classes.item,
                                                        activeRoute(path, location.pathname) && classes.itemActiveItem,
                                                    )}
                                                    onClick={() => {
                                                        setSubMenuOpen(
                                                            new Map(
                                                                subMenuOpen
                                                                    .set("tours", false)
                                                                    .set("help", false)
                                                                    .set("stats", false)
                                                                    .set("story", false)
                                                                    .set("survey", false),
                                                            ),
                                                        );
                                                        setAnchorEl(null);
                                                    }}
                                                >
                                                    <Tooltip title={childId}>
                                                        <ListItemIcon className={classes.itemIcon}>{icon}</ListItemIcon>
                                                    </Tooltip>
                                                    <ListItemText
                                                        classes={{
                                                            primary: classes.itemPrimary,
                                                        }}
                                                    >
                                                        {childId}
                                                    </ListItemText>
                                                </MenuItem>
                                            ),
                                    )}
                                </React.Fragment>
                            ))}
                        </>
                    ) : null}
                    {subMenuOpen.get("survey") ? (
                        <>
                            {surveyCategories.map(({ id, children }) => (
                                <React.Fragment key={id}>
                                    {children.map(
                                        ({ id: childId, icon, path, permission }) =>
                                            hasPermission(permission as Permission) && (
                                                <MenuItem
                                                    component={Link}
                                                    to={path}
                                                    key={childId}
                                                    className={clsx(
                                                        classes.item,
                                                        activeRoute(path, location.pathname) && classes.itemActiveItem,
                                                        "nav" + childId.split(" ").join("") + "Button",
                                                    )}
                                                    onClick={() => {
                                                        setSubMenuOpen(
                                                            new Map(
                                                                subMenuOpen
                                                                    .set("tours", false)
                                                                    .set("help", false)
                                                                    .set("stats", false)
                                                                    .set("story", false)
                                                                    .set("survey", false),
                                                            ),
                                                        );
                                                        setAnchorEl(null);
                                                    }}
                                                >
                                                    <Tooltip title={childId}>
                                                        <ListItemIcon className={classes.itemIcon}>{icon}</ListItemIcon>
                                                    </Tooltip>
                                                    <ListItemText
                                                        classes={{
                                                            primary: classes.itemPrimary,
                                                        }}
                                                    >
                                                        {childId}
                                                    </ListItemText>
                                                </MenuItem>
                                            ),
                                    )}
                                </React.Fragment>
                            ))}
                        </>
                    ) : null}
                    {subMenuOpen.get("stats") ? (
                        <>
                            {statsCategories.map(({ id, children }) => (
                                <React.Fragment key={id}>
                                    {children.map(
                                        ({ id: childId, icon, path, permission }) =>
                                            hasPermission(permission as Permission) && (
                                                <MenuItem
                                                    component={Link}
                                                    to={path}
                                                    key={childId}
                                                    className={clsx(
                                                        classes.item,
                                                        activeRoute(path, location.pathname) && classes.itemActiveItem,
                                                    )}
                                                    onClick={() => {
                                                        setSubMenuOpen(
                                                            new Map(
                                                                subMenuOpen
                                                                    .set("tours", false)
                                                                    .set("help", false)
                                                                    .set("stats", false)
                                                                    .set("story", false)
                                                                    .set("survey", false),
                                                            ),
                                                        );
                                                        setAnchorEl(null);
                                                    }}
                                                >
                                                    <Tooltip title={childId}>
                                                        <ListItemIcon className={classes.itemIcon}>{icon}</ListItemIcon>
                                                    </Tooltip>
                                                    <ListItemText
                                                        classes={{
                                                            primary: classes.itemPrimary,
                                                        }}
                                                    >
                                                        {childId}
                                                    </ListItemText>
                                                </MenuItem>
                                            ),
                                    )}
                                </React.Fragment>
                            ))}
                        </>
                    ) : null}
                </Paper>
            </Popper>
            <TutorialVideosDialog open={videosOpen} setOpen={setVideosOpen} />
            <SubscriptionContactDialog open={subDialogOpen} setOpen={setSubDialogOpen} />
            <SubscriptionExpiredDialog open={subExpiredDialogOpen} setOpen={setSubExpiredDialogOpen} />
        </>
    );
}
export default withStyles(styles)(Navigator);
